/**
 * useTelematics · Driver App · Milestone B
 * ========================================
 * Captures DeviceMotion (accelerometer) + Geolocation (speed) on the driver's
 * device, batches them locally every 60 seconds, and POSTs to
 * /driver/telematics/sample. The backend reduces the batch into a fresh
 * driving score that the My Metrics card consumes via its 30s poll.
 *
 * Browser quirks handled:
 *   • iOS 13+ requires explicit permission via DeviceMotionEvent.requestPermission()
 *     — we expose a `requestPermission()` returning whether the user granted it.
 *   • Listeners are removed on unmount + when paused.
 *   • Magnitudes are computed from total acceleration vector to avoid losing
 *     spikes that show only on Y or Z (e.g. potholes).
 *
 * Usage:
 *   const { enabled, supported, permission, request, lastScore } = useTelematics();
 *   <Button onClick={request}>Enable Safety Tracking</Button>
 *
 * Local-storage opt-in (`rtg_telematics_enabled`) persists the choice across
 * sessions so drivers don't re-grant every login.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";

const LS_KEY = "rtg_telematics_enabled";
const BATCH_INTERVAL_MS = 60_000;
// Sample no more than ~10x/sec to keep payload small.
const SENSOR_THROTTLE_MS = 100;
// Default urban speed limit when GPS doesn't expose one.
const DEFAULT_LIMIT_KPH = 70;

function supportsMotion() {
  return typeof window !== "undefined" && typeof window.DeviceMotionEvent !== "undefined";
}
function supportsGeo() {
  return typeof navigator !== "undefined" && typeof navigator.geolocation !== "undefined";
}

export default function useTelematics({ disabled = false } = {}) {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; }
  });
  const [permission, setPermission] = useState("unknown");
  const [lastScore, setLastScore] = useState(null);
  const accelBufRef = useRef([]);
  const speedBufRef = useRef([]);
  const lastSampleRef = useRef(0);
  const geoWatchRef = useRef(null);
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const supported = supportsMotion() && supportsGeo();

  // Persist opt-in to localStorage so a driver doesn't have to re-enable on
  // every login. Cleared explicitly via setEnabled(false).
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, enabled ? "1" : "0"); } catch { /* no-op */ }
  }, [enabled]);

  // -- accelerometer handler -------------------------------------------------
  const onMotion = useCallback((e) => {
    const now = Date.now();
    if (now - lastSampleRef.current < SENSOR_THROTTLE_MS) return;
    lastSampleRef.current = now;
    const a = e.accelerationIncludingGravity || e.acceleration || {};
    const x = a.x || 0, y = a.y || 0, z = a.z || 0;
    // total magnitude minus gravity (≈9.8) to get NET force on the vehicle.
    const total = Math.sqrt(x * x + y * y + z * z);
    const magnitude = Math.max(0, total - 9.8);
    // Estimate duration_ms from the interval since last sample.
    const dur = SENSOR_THROTTLE_MS;
    accelBufRef.current.push({ magnitude: Number(magnitude.toFixed(2)), duration_ms: dur });
    // Cap local buffer to 200 events to bound memory.
    if (accelBufRef.current.length > 200) accelBufRef.current.shift();
  }, []);

  // -- geolocation handler ---------------------------------------------------
  const onGeo = useCallback((pos) => {
    const c = pos.coords || {};
    if (c.speed == null) return; // browsers without speed data
    const kph = c.speed * 3.6;
    speedBufRef.current.push({
      speed_kph: Number(kph.toFixed(1)),
      limit_kph: DEFAULT_LIMIT_KPH,  // upgrade later with reverse-geocode road limits
      ts: Date.now(),
    });
    if (speedBufRef.current.length > 200) speedBufRef.current.shift();
  }, []);

  // -- batch upload to backend (every 60s) -----------------------------------
  const flush = useCallback(async () => {
    const accel = accelBufRef.current.slice();
    const speed = speedBufRef.current.slice();
    if (accel.length === 0 && speed.length === 0) return;
    accelBufRef.current = [];
    speedBufRef.current = [];
    try {
      const { data } = await api.post("/driver/telematics/sample", {
        acceleration_data: accel,
        speed_data: speed,
        client_ts: new Date().toISOString(),
        duration_ms: BATCH_INTERVAL_MS,
      });
      if (mountedRef.current && data?.driving_score != null) {
        setLastScore(data.driving_score);
      }
    } catch {
      // network failure → put samples back so the next flush retries them.
      accelBufRef.current = [...accel, ...accelBufRef.current].slice(-400);
      speedBufRef.current = [...speed, ...speedBufRef.current].slice(-400);
    }
  }, []);

  // -- wiring ----------------------------------------------------------------
  useEffect(() => {
    mountedRef.current = true;
    if (disabled || !enabled || !supported) return;
    window.addEventListener("devicemotion", onMotion, { passive: true });
    try {
      geoWatchRef.current = navigator.geolocation.watchPosition(
        onGeo,
        () => { /* permission denied — silent; user already saw the prompt */ },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
      );
    } catch { /* no-op */ }
    intervalRef.current = setInterval(flush, BATCH_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("devicemotion", onMotion);
      if (geoWatchRef.current != null) {
        try { navigator.geolocation.clearWatch(geoWatchRef.current); } catch { /* no-op */ }
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, disabled, supported, onMotion, onGeo, flush]);

  // -- explicit permission flow (iOS) ----------------------------------------
  const request = useCallback(async () => {
    if (!supported) {
      setPermission("unsupported");
      return false;
    }
    let motionGranted = true;
    // iOS Safari requires explicit user-gesture permission for DeviceMotion.
    if (typeof DeviceMotionEvent !== "undefined" && typeof DeviceMotionEvent.requestPermission === "function") {
      try {
        const res = await DeviceMotionEvent.requestPermission();
        motionGranted = res === "granted";
      } catch {
        motionGranted = false;
      }
    }
    // Geolocation is requested implicitly when watchPosition starts; we kick
    // it once here so the prompt appears in the same gesture.
    let geoGranted = false;
    try {
      await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => { geoGranted = true; resolve(); },
          () => { geoGranted = false; resolve(); },
          { timeout: 8000 }
        );
      });
    } catch { /* no-op */ }
    const ok = motionGranted && geoGranted;
    setPermission(ok ? "granted" : "denied");
    if (ok) setEnabled(true);
    return ok;
  }, [supported]);

  const disable = useCallback(() => {
    setEnabled(false);
  }, []);

  return { enabled, supported, permission, request, disable, lastScore };
}
